import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, ViewEncapsulation } from '@angular/core';
import { defaultHypersceneIfcMergeMatrix, HypersceneIfcMergeItem } from '../hyperscene-advanced/models/merge-settings';
import { HypersceneIngestAdapterService } from '../hyperscene-advanced/services/hyperscene-ingest-adapter.service';

@Component({
  selector: 'hyperscene-ifc-merge-tools',
  templateUrl: './merge-tools.component.html',
  styleUrls: ['./merge-tools.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class IfcMergeToolsComponent {
  public ifcMergeItems: HypersceneIfcMergeItem[] = [];
  public ifcMergeWorkers: number = 2;
  public ifcMergeInProgress: boolean = false;
  public ifcMergeCommandCopied: boolean = false;
  public ifcMergeStatusMessage: string = '';
  public ifcMergeError: string | null = null;

  private readonly ingestAdapter: HypersceneIngestAdapterService = new HypersceneIngestAdapterService();

  public constructor(
    private readonly changeDetector: ChangeDetectorRef,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  public get ifcMergeRunCommand(): string {
    return this.ingestAdapter.createIfcMergeCommand(
      this.ifcMergeItems.map((item: HypersceneIfcMergeItem) => ({
        fileName: item.file.name,
        matrix: item.matrix,
      })),
      'hyperscene-ifc-merge.py',
      'merged.ifc',
      undefined,
      this.ifcMergeWorkers
    );
  }

  public onIfcMergeWorkersChanged(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    const value: number = Number(event.target.value);
    this.ifcMergeWorkers = Number.isInteger(value) ? Math.max(1, value) : 1;
    this.ifcMergeCommandCopied = false;
  }

  public onIfcMergeFilesChanged(event: Event): void {
    if (this.ifcMergeInProgress || !(event.target instanceof HTMLInputElement)) {
      return;
    }

    const selectedFiles: File[] = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (selectedFiles.length === 0) {
      return;
    }

    const existingFiles: Set<string> = new Set(
      this.ifcMergeItems.map(
        (item: HypersceneIfcMergeItem) => `${item.file.name}:${item.file.size}:${item.file.lastModified}`
      )
    );
    const newItems: HypersceneIfcMergeItem[] = selectedFiles
      .filter((file: File) => !existingFiles.has(`${file.name}:${file.size}:${file.lastModified}`))
      .map((file: File) => ({
        file,
        matrix: defaultHypersceneIfcMergeMatrix(),
      }));
    this.ifcMergeItems = [...this.ifcMergeItems, ...newItems];
    this.ifcMergeCommandCopied = false;
    this.ifcMergeError = null;
    this.ifcMergeStatusMessage =
      this.ifcMergeItems.length < 2
        ? 'Select at least two IFC files to generate a merge command.'
        : `${this.ifcMergeItems.length} IFC files ready for script generation.`;
    this.changeDetector.markForCheck();
  }

  public clearIfcMergeFiles(): void {
    if (this.ifcMergeInProgress) {
      return;
    }

    this.ifcMergeItems = [];
    this.ifcMergeCommandCopied = false;
    this.ifcMergeError = null;
    this.ifcMergeStatusMessage = 'Select at least two IFC files for merge.';
    this.changeDetector.markForCheck();
  }

  public onIfcMergeMatrixChanged(index: number, matrixIndex: number, event: Event): void {
    const mergeItem: HypersceneIfcMergeItem | undefined = this.ifcMergeItems[index];
    if (!(event.target instanceof HTMLInputElement) || mergeItem === undefined) {
      return;
    }

    const value: number = Number(event.target.value);
    const matrix: number[] = [...mergeItem.matrix];
    matrix[matrixIndex] = Number.isFinite(value) ? value : 0;
    this.ifcMergeItems = this.ifcMergeItems.map((item: HypersceneIfcMergeItem, itemIndex: number) =>
      itemIndex === index ? { ...item, matrix } : item
    );
    this.ifcMergeCommandCopied = false;
  }

  public async copyIfcMergeCommand(): Promise<void> {
    try {
      const clipboard: Clipboard | undefined = this.document.defaultView?.navigator?.clipboard;
      if (clipboard === undefined) {
        this.ifcMergeError = 'Clipboard access is not available in this browser context.';
        this.changeDetector.markForCheck();
        return;
      }

      await clipboard.writeText(this.ifcMergeRunCommand);
      this.ifcMergeCommandCopied = true;
      this.ifcMergeError = null;
      this.ifcMergeStatusMessage = 'Run command copied to clipboard.';
      this.changeDetector.markForCheck();
    } catch {
      this.ifcMergeError = 'Clipboard access is not available in this browser context.';
      this.changeDetector.markForCheck();
    }
  }

  public downloadIfcMergeScript(): void {
    if (this.ifcMergeInProgress) {
      return;
    }

    this.ifcMergeInProgress = true;
    this.ifcMergeCommandCopied = false;
    try {
      const script: Blob = this.ingestAdapter.createIfcMergeScript();
      const blobUrl: string = URL.createObjectURL(script);
      const link: HTMLAnchorElement = this.document.createElement('a');
      link.href = blobUrl;
      link.download = 'hyperscene-ifc-merge.py';
      link.click();
      URL.revokeObjectURL(blobUrl);
      this.ifcMergeStatusMessage =
        this.ifcMergeItems.length >= 2
          ? 'Static Python script downloaded. Copy the command and run it next to the selected IFC files.'
          : 'Static Python script downloaded. Select at least two IFC files to generate a merge command.';
      this.ifcMergeError = null;
    } finally {
      this.ifcMergeInProgress = false;
      this.changeDetector.markForCheck();
    }
  }
}
